/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import AssertionError from 'assertion-error';
import isNil from 'lodash/isNil';

import ScheduleContent, { ContentType } from '../../schedule/schedule-content';
import ScheduleInfo from '../../schedule/schedule-info';
import { EmptyContentController } from './empty-content-controller';
import { LayoutContentController } from './layout-content-controller';
import { MediumContentController } from './medium-content-controller';
import { PlaylistDeterministicContentController } from './playlist-deterministic-content-controller';
import { PlaylistContentController } from './playlist-content-controller';
import RegionContentController from './region-content-controller';

/**
 * @import { ContentController, PlaylistContentControllerOpts, ContentControllerOpts, ContentControllerFactory as IContentControllerFactory } from '.'
 * @import BrowserCapabilities from '../../capabilities/browser-capabilities'
 * @import { Content, Medium, Layout } from '../content';
 * @import { Logger } from '../../logger/logger'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { ViewerRegistry } from '../../viewer/registry'
 * @import StretchEnum from '../../stretch/stretch-enum'
 * @import { ScheduleContentInfo } from '../../schedule/schedule-info'
 */

/**
 * @implements {IContentControllerFactory}
 */
export default class ContentControllerFactory {
  /**
   * @param {BrowserCapabilities} browserCapabilities
   * @param {Logger} logger
   * @param {UnixTimestamp} unixTimestamp
   * @param {ViewerRegistry} viewerRegistry
   */
  constructor(browserCapabilities, logger, unixTimestamp, viewerRegistry) {
    this.browserCapabilities = browserCapabilities;
    this.logger = logger;
    this.unixTimestamp = unixTimestamp;
    this.viewerRegistry = viewerRegistry;
  }

  /**
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {ContentControllerOpts} opts
   * @returns {ContentController}
   */
  newLayoutContentController(el, content, opts) {
    return new LayoutContentController(
      this,
      opts.contentInfo,
      content,
      el,
      opts.volume
    );
  }

  /**
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {ContentControllerOpts} opts
   * @returns {ContentController}
   */
  newMediumContentController(el, content, opts) {
    const medium = /** @type {Medium} */ (content.medium);
    const viewerType = this.viewerRegistry.find(medium.type) ?? undefined;
    return new MediumContentController(
      this.browserCapabilities,
      content,
      opts.contentInfo,
      el,
      new ScheduleInfo({
        trackDuration: opts.trackDuration ?? content.medium?.playDuration,
        medium,
        stretchEnum: getStretchEnum(opts.contentInfo, medium),
        viewerType,
        volume: opts.volume
      }),
      viewerType
    );
  }

  /**
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {PlaylistContentControllerOpts} opts
   * @returns {ContentController}
   */
  newPlaylistContentController(el, content, opts) {
    if (content.playlist?.deterministic) {
      return new PlaylistDeterministicContentController(
        this,
        opts.contentInfo,
        content,
        this.logger,
        el,
        opts.volume,
        this.unixTimestamp
      );
    }
    if (opts.disableAutoRewind === undefined) {
      throw new AssertionError(
        'disableAutoRewind is required options for PlaylistContentController'
      );
    }
    return new PlaylistContentController(
      this,
      opts.contentInfo,
      content,
      opts.disableAutoRewind,
      this.logger,
      el,
      opts.volume,
      this.unixTimestamp
    );
  }

  /** @type {IContentControllerFactory['create']} */
  create(el, content, opts) {
    if (!content) {
      return new EmptyContentController();
    }

    switch (ScheduleContent.type(content)) {
      case ContentType.Layout:
        return this.newLayoutContentController(el, content, opts);
      case ContentType.Medium:
        return this.newMediumContentController(el, content, opts);
      case ContentType.Playlist:
        return this.newPlaylistContentController(el, content, {
          ...opts,
          // Nastavení disableAutoRewind na true, aby playlist po každé
          // iteraci mohl emitovat event ended.
          disableAutoRewind: true
        });
      default:
        throw new AssertionError('Unknown content type.');
    }
  }

  /** @type {IContentControllerFactory['createForRegion']} */
  createForRegion(el, content, opts) {
    if (!content) {
      return new EmptyContentController();
    }
    /** @type {ContentController | undefined} */
    let contentController;
    /** @type {number | undefined} */
    let duration;
    switch (ScheduleContent.type(content)) {
      case ContentType.Layout:
        contentController = this.newLayoutContentController(el, content, opts);
        duration = /** @type {Layout} */ (content.layout).playDuration;
        break;
      case ContentType.Medium:
        contentController = this.newMediumContentController(el, content, {
          ...opts,
          // Pro medium v regionu (top level) se bere hlasitost ze samotného media.
          volume: content.medium?.volume ?? 1
        });
        duration = /** @type {Medium} */ (content.medium).playDuration;
        break;
      case ContentType.Playlist:
        // Playlist se zaobaluje do RegionContentController, kdyby případně simulator překločil
        // maximální čas simulace, tak aby jej měl kdo spustit znovu.
        contentController = this.newPlaylistContentController(el, content, {
          ...opts,
          // Pro playlist v regionu (top level) se bere hlavní hlasitost z hlasitosti playlistu.
          volume: content.playlist?.volume ?? 1,
          // Nastavení disableAutoRewind na false, aby se prováděl automatický rewind
          // na začátek playlistu. Autorewind by mohl dělat i RegionContentController,
          // ale pak by první content nikdy nebyl preloadovaný.
          disableAutoRewind: false
        });
        duration = 0;
        break;
      default:
        throw new AssertionError('Unknown content type.');
    }

    return new RegionContentController(
      content,
      contentController,
      duration,
      el
    );
  }
}

/**
 * Zjistí roztažení, které se má nastavit médiu.
 * @param {ScheduleContentInfo} contentInfo
 * @param {Medium} medium
 * @returns {StretchEnum} Roztažení.
 */
export function getStretchEnum(contentInfo, medium) {
  const mediumStretchEnum = medium.stretchEnum;
  if (!isNil(mediumStretchEnum)) {
    return mediumStretchEnum;
  }
  return contentInfo.stretchEnum;
}
